import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from '../lib/api';
import { qk } from '../lib/queryKeys';
import type { EntriesResponse, EntryDto } from '../types/api';

export function invalidateTimeData(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: qk.entries });
  void qc.invalidateQueries({ queryKey: qk.activity });
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
  void qc.invalidateQueries({ queryKey: ['report'] });
}

export function useEntries() {
  return useQuery({
    queryKey: qk.entries,
    queryFn: () => apiGet<EntriesResponse>('/api/entries'),
    staleTime: 15_000,
  });
}

export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { projectId?: string | null; taskId?: string | null; description?: string }) =>
      apiPost<EntryDto>('/api/entries/start', body),
    onSettled: () => invalidateTimeData(qc),
  });
}

export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; description?: string; projectId?: string | null; taskId?: string | null }) =>
      apiPost<EntryDto>(`/api/entries/${vars.id}/stop`, {
        description: vars.description,
        projectId: vars.projectId,
        taskId: vars.taskId,
      }),
    onSettled: () => invalidateTimeData(qc),
  });
}

export function useUpdateEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      meta?: { description?: string; projectId?: string | null; taskId?: string | null };
      times?: { start?: number; stop?: number };
    }) => apiPatch<EntryDto>(`/api/entries/${vars.id}`, { meta: vars.meta, times: vars.times }),
    onSettled: () => invalidateTimeData(qc),
  });
}

export function useDeleteEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/entries/${id}`),
    onSettled: () => invalidateTimeData(qc),
  });
}

export function useForceStopEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<EntryDto>(`/api/entries/${id}/force-stop`),
    onSettled: () => invalidateTimeData(qc),
  });
}

export function useCopyEntryToMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<EntryDto>(`/api/entries/${id}/copy-to-me`),
    onSettled: () => invalidateTimeData(qc),
  });
}

export function useAddManualEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      ownerId: string;
      projectId?: string | null;
      taskId: string;
      description: string;
      start: number;
      stop: number;
    }) => apiPost<EntryDto>('/api/entries/manual', body),
    onSettled: () => invalidateTimeData(qc),
  });
}
